import ChartControls from '@root/joinroot.com/src/components/covid-driving/chart-section/chart-controls';
import ChartLegend from '@root/joinroot.com/src/components/covid-driving/chart-section/chart-legend';
import EventListing from '@root/joinroot.com/src/components/covid-driving/chart-section/event-listing';
import LineChart from '@root/joinroot.com/src/components/covid-driving/chart-section/line-chart';
import PropTypes from '@root/vendor/prop-types';
import React, { useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import eventsFileData from '@root/joinroot.com/src/assets/data/driving-data/events.json';
import iconAccident from '@root/gatsby-contentful-core/src/assets/icons/accident.svg';
import { Colors, StyleSheet } from '@root/core/src/utils/styles';
import { H2, H3, P1 } from '@root/gatsby-contentful-core/src/components/text';
import { METRICS } from '@root/joinroot.com/src/components/covid-driving';
import { localDateFromString, stringifyDate } from '@root/joinroot.com/src/components/covid-driving/covid-driving-utils';
import { toRgba } from '@root/gatsby-contentful-core/src/utils/colors';

export default function CovidDrivingChart({
  dataToDisplay,
  endDate,
  metric,
  startDate,
  trackClick = () => { },
}) {
  const [isUS2019On, setIsUS2019On] = useState(true);
  const [isUS2020On, setIsUS2020On] = useState(true);
  const [selectedState, setSelectedState] = useState('');

  const stayHomeLabel = 'Stay-at-home orders';

  const METRIC_HEADLINES = {
    [METRICS.TRIP_COUNT]: 'Trips taken per day',
    [METRICS.TRIP_MILES]: 'Miles driven per day',
    [METRICS.TRIP_MINUTES]: 'Time spent driving',
  };

  const METRICS_TO_Y_LEGEND = {
    [METRICS.TRIP_COUNT]: 'Trips per driver',
    [METRICS.TRIP_MILES]: 'Trip miles per driver',
    [METRICS.TRIP_MINUTES]: 'Driving time per driver (minutes)',
  };

  const METRICS_TO_Y_MAX = {
    [METRICS.TRIP_COUNT]: 6,
    [METRICS.TRIP_MILES]: 50,
    [METRICS.TRIP_MINUTES]: 120,
  };

  const METRICS_TO_Y_TICK_VALUES = {
    [METRICS.TRIP_COUNT]: [0, 1, 2, 3, 4, 5, 6],
    [METRICS.TRIP_MILES]: [0, 10, 20, 30, 40, 50],
    [METRICS.TRIP_MINUTES]: [0, 30, 60, 90, 120],
  };

  const LINE_COLORS = {
    'us2019': Colors.gray20(),
    'us2020': Colors.rootOrange(),
    'state': Colors.gray50(),
    'stayHome': '#AED7FF',
  };

  const makeDataObj = (id, color, data) => {
    if (!(id && data && data.length > 0)) { return null; }

    const formatDates = data.map(({ x, y }) => {
      const newX = x ? stringifyDate(x) : x;
      return {
        x: newX,
        y,
      };
    });

    return {
      id,
      color,
      data: formatDates,
    };
  };

  const lineChartData = [];

  if (dataToDisplay.national) {
    if (isUS2019On) { lineChartData.push(makeDataObj('U.S. 2019', LINE_COLORS.us2019, dataToDisplay.national['2019'].byDay)); }
    if (isUS2020On) { lineChartData.push(makeDataObj('U.S. 2020', LINE_COLORS.us2020, dataToDisplay.national['2020'].byDay)); }
  }

  if (dataToDisplay.states && selectedState) {
    lineChartData.push(makeDataObj(selectedState, LINE_COLORS.state, dataToDisplay.states[selectedState]['2020'].byDay));
  }

  function generateStayAtHomeZone(zoneStart, zoneEnd) {
    const selectedStartDateIsBeyondZone = startDate > localDateFromString(zoneEnd);
    const selectedEndDateIsBeyondZone = endDate < localDateFromString(zoneStart);
    const selectedStartDateIsWithinZone = startDate > localDateFromString(zoneStart) && startDate < localDateFromString(zoneEnd);
    const selectedEndDateIsWithinZone = endDate > localDateFromString(zoneStart) && endDate < localDateFromString(zoneEnd);

    if (selectedStartDateIsBeyondZone || selectedEndDateIsBeyondZone) { return; }

    if (selectedStartDateIsWithinZone) {
      zoneStart = stringifyDate(startDate);
    }
    if (selectedEndDateIsWithinZone) {
      zoneEnd = stringifyDate(endDate);
    }

    lineChartData.push({
      id: stayHomeLabel,
      color: LINE_COLORS.stayHome,
      data: [
        {
          x: zoneStart,
          y: 0,
        },
        {
          x: zoneStart,
          y: METRICS_TO_Y_MAX[metric],
        },
        {
          x: zoneEnd,
          y: METRICS_TO_Y_MAX[metric],
        },
        {
          x: zoneEnd,
          y: 0,
        },
      ],
    });
  }

  generateStayAtHomeZone('2020-03-19', '2020-03-23');

  const handleChange2019 = (newValue) => {
    trackClick('US_2019_CHECKBOX');
    setIsUS2019On(newValue);
  };

  const handleChange2020 = (newValue) => {
    trackClick('US_2020_CHECKBOX');
    setIsUS2020On(newValue);
  };

  const handleChangeSelectedState = (newValue) => {
    trackClick('US_STATE_SELECTOR');
    setSelectedState(newValue);
  };

  return (
    <section css={styles.section}>
      <H2>{METRIC_HEADLINES[metric]}</H2>
      <P1 css={styles.paragraph}>Daily driving dropped significantly beginning in early March, with particularly sharp declines as mid-March stay-at-home orders took effect, and as much as 80% of the country sheltering in place by the end of the month.</P1>
      <ChartControls
        dataToDisplay={dataToDisplay}
        handleChange2019={handleChange2019}
        handleChange2020={handleChange2020}
        handleChangeSelectedState={handleChangeSelectedState}
        isUS2019On={isUS2019On}
        isUS2020On={isUS2020On}
        selectedState={selectedState}
      />
      <ChartLegend
        lineChartData={lineChartData}
        stayHomeLabel={stayHomeLabel}
        yAxisLegend={METRICS_TO_Y_LEGEND[metric]}
      />
      <div css={styles.chartContainer}>
        <LineChart
          data={lineChartData}
          endDate={endDate}
          eventsFileData={eventsFileData}
          metric={metric}
          startDate={startDate}
          stayHomeLabel={stayHomeLabel}
          yAxisLegend={METRICS_TO_Y_LEGEND[metric]}
          yAxisMax={Number(METRICS_TO_Y_MAX[metric])}
          yAxisTickValues={METRICS_TO_Y_TICK_VALUES[metric]}
        />
        {lineChartData.length < 1 && (
          <div css={styles.noDataMessageBox}>
            <img
              alt={'car accident icon'}
              src={iconAccident}
            />
            <H3 css={styles.noDataMessage}>Uh-oh! We couldn&apos;t find any data based on your selection.</H3>
          </div>
        )}
      </div>
      <EventListing
        eventColor={LINE_COLORS.stayHome}
        eventsFileData={eventsFileData}
      />
    </section>
  );
}

CovidDrivingChart.propTypes = {
  dataToDisplay: PropTypes.object,
  endDate: PropTypes.instanceOf(Date),
  metric: PropTypes.string,
  metricUnits: PropTypes.string,
  startDate: PropTypes.instanceOf(Date),
  trackClick: PropTypes.func,
};

const styles = StyleSheet.create({
  section: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    ...Responsive.md({
      padding: '100px 20px',
    }),
  },
  paragraph: {
    maxWidth: 690,
    padding: '20px 0 30px',
    textAlign: 'center',
    ...Responsive.md({
      padding: '50px 0',
    }),
  },
  chartContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: 1200,
    height: 400,
    cursor: 'crosshair',
    ...Responsive.md({
      height: 600,
    }),
    // This styles the stay at home zone
    '& path:not([fill="#AED7FF"])': {
      fillOpacity: '0',
    },
    '& path[stroke="rgb(174, 215, 255)"]': {
      opacity: '0',
    },
    '& circle[fill="#AED7FF"]': {
      opacity: '0',
    },
  },
  noDataMessageBox: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 450,
    padding: 30,
    backgroundColor: Colors.white(),
    borderRadius: 10,
    boxShadow: `0 6px 30px -4px ${toRgba(Colors.nearBlack(), 0.2)}`,
    textAlign: 'center',
  },
  noDataMessage: {
    marginTop: 14,
  },
});

